import { enableField, disableField } from '../../../actions';
import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Mp1SensorIds, Mp1SensorTitles, Mp2SensorIds, Mp2SensorTitles, TritonSensors, getSensorFieldId } from '../base-config-models';
import { PeakComboSensorFieldIds } from './config-models';
import { MonitoringPointElement } from 'app/shared/components/location-card/models/location-card.model';
import { LocationCardSettings } from '../../../reducers';
import { field } from '../../../core/field-schema';
import { required, pattern, min, max } from '../../../core/validator-builders';
import { PeakComboDevice } from '../models';
import {
    MaxVelocityMetricValuesNonParaFlow,
    MaxVelocityNonMetricValuesNonParaFlow,
} from 'app/shared/components/location-card/location-card.constants';

/**
 * Peak Combo sensor configuration
 */
export const peakComboSensor = (mp: 0 | 1, settings: LocationCardSettings, sensorData: PeakComboDevice): Section => {
    const isMetric = settings.isMetric;
    const maxVelocityOptions = isMetric ? MaxVelocityMetricValuesNonParaFlow : MaxVelocityNonMetricValuesNonParaFlow;

    return {
        info: {
            id: mp === 0 ? Mp1SensorIds[TritonSensors.PeakCombo] : Mp2SensorIds[TritonSensors.PeakCombo],
            title: mp === 0 ? Mp1SensorTitles[TritonSensors.PeakCombo] : Mp2SensorTitles[TritonSensors.PeakCombo],
            expandable: true,
            displayTitle: true,
            expanded: false,
        },
        rows: [
            // Flow Type & Physical Offset Row
            {
                info: {
                    id: 'flow-offset-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.UltrasonicFlowType, mp), 'ultrasonicFlowType', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FLOW',
                        value: sensorData ? sensorData.ultrasonicFlowType : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.FlowTypes,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        panelClass: 'custom-width required',
                    }),
                    field.number(getSensorFieldId(PeakComboSensorFieldIds.PhysicalOffset, mp), 'physicalOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_PHYSICAL',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.physicalOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'custom-width required',
                    }),
                ],
            },

            // Ultrasonic Measurement Toggle Row
            {
                info: {
                    id: 'ultrasonic-enable-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.UltrasonicEnable, mp), 'ultrasonicEnable', {
                        value: sensorData ? sensorData.ultrasonicEnable : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_MEASUREMENT',
                        panelClass: 'custom-slide-toggle highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.UltrasonicElectronicOffset, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.UltrasonicElectronicOffset, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.UltrasonicDeadband, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.UltrasonicDeadband, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.UltrasonicStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.UltrasonicStoreData, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Ultrasonic Settings Row
            {
                info: {
                    id: 'ultrasonic-settings-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.number(getSensorFieldId(PeakComboSensorFieldIds.UltrasonicElectronicOffset, mp), 'ultrasonicElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.ultrasonicElectronicOffset : 0,
                        disabled: sensorData ? !sensorData.ultrasonicEnable : false,
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.UltrasonicDeadband, mp), 'ultrasonicDeadband', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DEAD_BAND',
                        value: sensorData ? sensorData.ultrasonicDeadband : 0,
                        disabled: sensorData ? !sensorData.ultrasonicEnable : false,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.DeadBandList,
                        panelClass: 'width-30 required',
                    }),
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.UltrasonicStoreData, mp), 'ultrasonicStoreData', {
                        disabled: sensorData ? !sensorData.ultrasonicEnable : false,
                        value: sensorData ? sensorData.ultrasonicStoreData : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                ],
            },

            // Pressure Measurement Toggle Row
            {
                info: {
                    id: 'pressure-enable-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.PressureEnable, mp), 'pressureEnable', {
                        value: sensorData ? sensorData.pressureEnable : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_PRESSURE',
                        panelClass: 'custom-slide-toggle highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.PressureElectronicOffset, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.PressureElectronicOffset, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.PressureStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.PressureStoreData, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.PressureCalibration, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.PressureCalibration, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Pressure Settings Row
            {
                info: {
                    id: 'pressure-settings-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(getSensorFieldId(PeakComboSensorFieldIds.PressureElectronicOffset, mp), 'pressureElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        value: sensorData ? sensorData.pressureElectronicOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                    field.checkbox(getSensorFieldId(PeakComboSensorFieldIds.PressureCalibration, mp), 'pressureCalibration', {
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        value: sensorData ? sensorData.pressureCalibration : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_AUTO_CALIBER',
                        panelClass: 'each-Item item-flex-center',
                    }),
                ],
            },

            // Pressure Store Data Row
            {
                info: {
                    id: 'pressure-store-data-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.PressureStoreData, mp), 'pressureStoreData', {
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        value: sensorData ? sensorData.pressureStoreData : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                ],
            },

            // Velocity Measurement Toggle Row
            {
                info: {
                    id: 'velocity-enable-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.VelocityEnable, mp), 'velocityEnable', {
                        value: sensorData ? sensorData.velocityEnable : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_VELOCITY',
                        panelClass: 'custom-slide-toggle highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocitySensitivity, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocitySensitivity, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityDirection, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityDirection, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityGain, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityGain, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityMaximum, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityMaximum, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityStoreData, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityScrubbing, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityScrubbing, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityFlowType, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.VelocityFlowType, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.TransmitFrequency, mp) })
                                    : disableField({ fieldId: getSensorFieldId(PeakComboSensorFieldIds.TransmitFrequency, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Velocity Sensitivity & Direction Row
            {
                info: {
                    id: 'velocity-sens-dir-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.VelocitySensitivity, mp), 'velocitySensitivity', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSITIVITY',
                        value: sensorData ? sensorData.velocitySensitivity : 10,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        validators: [required()],
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        optionsSelector: SelectorIdentifier.SensitivityList,
                        panelClass: 'each-Item required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.VelocityDirection, mp), 'velocityDirection', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DIRECTION',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityDirection : 1,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.DirectionList,
                        panelClass: 'each-Item required',
                    }),
                    field.number(getSensorFieldId(PeakComboSensorFieldIds.VelocityGain, mp), 'velocityGain', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORGAIN',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityGain : 0.9,
                        validators: [
                            required(),
                            min({ value: 0 }),
                            max({ value: 2 }),
                            pattern({
                                value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: 'CUSTOMER_EDITOR.RAIN_PROFILE.STORM_EVENT.INVALID_INPUT_TXT',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                ],
            },

            // Velocity Maximum Row
            {
                info: {
                    id: 'velocity-maximum-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.VelocityMaximum, mp), 'velocityMaximum', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_VALUE_MAXIMUM',
                        value: sensorData && maxVelocityOptions.find((v) => v.value === sensorData.velocityMaximum)
                            ? sensorData.velocityMaximum
                            : 0,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MaxVelocity,
                        panelClass: 'full-width required',
                    }),
                ],
            },

            // Velocity Flow Type & Frequency Row
            {
                info: {
                    id: 'velocity-flow-freq-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.VelocityFlowType, mp), 'velocityFlowType', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FLOW',
                        value: sensorData ? sensorData.velocityFlowType : 0,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        optionsSelector: SelectorIdentifier.FlowTypes,
                        panelClass: 'each-Item',
                        events: {
                            // onChange: (value) => [setFieldValue({ fieldName: PeakComboSensorFieldIds.UltrasonicFlowType, value })],
                        },
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.TransmitFrequency, mp), 'transmitFrequency', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        placeholder: 'ADD_EDIT.TRANSMIT_FREQUENCY',
                        value: sensorData ? sensorData.velocityFrequency : 1,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        optionsSelector: SelectorIdentifier.TransmitFrequencyList,
                        panelClass: 'each-Item',
                    }),
                ],
            },

            // Velocity Store Data & Scrubbing Row
            {
                info: {
                    id: 'velocity-store-scrub-row',
                    panelClass: 'custom-50p pad-b-15',
                },
                items: [
                    field.checkbox(getSensorFieldId(PeakComboSensorFieldIds.VelocityScrubbing, mp), 'velocityScrubbing', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityScrubbing : true,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SCRUBBLING',
                        panelClass: 'each-Item',
                    }),
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.VelocityStoreData, mp), 'velocityStoreData', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityStoreData : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'each-Item item-flex-center custom-slide-toggle',
                    }),
                ],
            },

            // Info Text Row
            {
                info: {
                    id: 'info-text-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    {
                        id: 'info-text',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'infoText',
                        label: 'ADD_EDIT.ADD_EDIT_TEXT',
                        panelClass: 'toggle toggle-text input-text font-600',
                    },
                ],
            },

            // Temperature Storage & Sensor Row
            {
                info: {
                    id: 'temperature-store-sensor-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(getSensorFieldId(PeakComboSensorFieldIds.TemperatureStoreData, mp), 'temperatureStoreData', {
                        value: sensorData ? sensorData.temperatureStoreData : true,
                        placeholder: 'ADD_EDIT.ACTIVE_TOGGLE_STORE',
                        panelClass: 'each-Item custom-slide-toggle item-flex-center',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(PeakComboSensorFieldIds.Sensor, mp), 'sensor', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        value: sensorData ? sensorData.temperatureSensor : 1,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.TemperatureSensors,
                        panelClass: 'each-Item full-width required',
                    }),
                ],
            },
        ],
    };
};
